import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  Upload,
  Plus,
  Search,
  Phone,
  Trash2,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  Info,
  GraduationCap,
  Loader2,
  UserX,
  RotateCcw,
  KeyRound,
  Pencil,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  User,
  Download,
  Calendar,
  HeartPulse,
  Users,
  Clock,
  Camera,
  Award,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Modal, Input, Select, Button } from '../../Components/ui';
import { useConfirm } from '../../Components/Feedback';
import FotoAluno from '../../Components/FotoAluno';
import AlunoBadges, { parseLaudos } from '../../Components/AlunoBadges';
import { corCardNivel } from '../../utils/niveis';
import { corDaAtividade } from '../../utils/corDaAtividade';
import { API_URL } from '../../api/config';

const diasSemana = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const horarios = ['HR 1', 'HR 2', 'HR 3'];
const turnos = ['Manhã', 'Tarde', 'Noite'];

// "alunos.inativado_em" vem do backend já como "AAAA-MM-DD" (dateStrings:
// ['DATE'] em db.js) — só reformata pra exibição, sem conversão de fuso.
const formatarDataBR = (dataISO) => {
  if (!dataISO) return null;
  const [ano, mes, dia] = String(dataISO).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : null;
};

// "alunos.data_nascimento" vem como "AAAA-MM-DD" (mesmo formato de
// inativado_em — dateStrings: ['DATE'] em db.js) — mesma lógica usada em
// AjusteGrade.js pra calcular idade a partir dessa data.
const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const match = String(dataNascimento).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const anoNasc = Number(match[1]),
    mesNasc = Number(match[2]),
    diaNasc = Number(match[3]);

  const hoje = new Date();
  let idade = hoje.getFullYear() - anoNasc;
  const mesAtual = hoje.getMonth() + 1;
  const aindaNaoFezAniversario =
    mesAtual < mesNasc || (mesAtual === mesNasc && hoje.getDate() < diaNasc);
  if (aindaNaoFezAniversario) idade -= 1;
  return idade;
};

// Rótulo de idade exibido/usado como valor de filtro — mesmo formato de
// AjusteGrade.js ("8 anos", "-" quando não há data de nascimento).
const rotuloIdade = (aluno) => {
  const idade = calcularIdade(aluno.data_nascimento);
  return idade == null ? '-' : `${idade} anos`;
};

// Rótulo de turno exibido/usado como valor de filtro.
const rotuloTurno = (aluno) => aluno.turno || '-';

// Filtro estilo Excel por valor específico de uma coluna (idade, turno, ...
// mesmo padrão de FiltroColunaSimples em Professores.js) — `excluidos` guarda
// os valores escondidos, vazio = sem filtro (mostra todos os valores).
function FiltroValorColuna({ opcoes, excluidos, onChange, titulo }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!aberto) return;
    const fecharSeFora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', fecharSeFora);
    return () => document.removeEventListener('mousedown', fecharSeFora);
  }, [aberto]);

  const alternar = (valor) => {
    onChange(
      excluidos.includes(valor) ? excluidos.filter((v) => v !== valor) : [...excluidos, valor],
    );
  };

  const ativo = excluidos.length > 0;

  return (
    <span className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        title={titulo}
        className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${ativo ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
      >
        <Filter className="w-3.5 h-3.5" fill={ativo ? 'currentColor' : 'none'} />
      </button>
      {aberto && (
        <div className="absolute z-30 top-full mt-1 left-0 w-32 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <div className="flex items-center justify-between px-2 pt-2 pb-1 border-b border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              Todas
            </button>
            <button
              type="button"
              onClick={() => onChange(opcoes)}
              className="text-[10px] font-medium text-gray-400 dark:text-gray-500 hover:underline"
            >
              Nenhuma
            </button>
          </div>
          <div className="px-2 py-1.5 space-y-1 max-h-48 overflow-y-auto">
            {opcoes.map((valor) => (
              <label
                key={valor}
                className="flex items-center gap-1.5 text-[11px] font-normal text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!excluidos.includes(valor)}
                  onChange={() => alternar(valor)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500/30"
                />
                <span className="truncate">{valor}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}

// Tela de gestão de alunos e matrículas: buscar/cadastrar/excluir aluno, ver e
// editar sua grade de matrículas, contatos de emergência, e importação em massa
// via Excel (a única via de cadastro de matrícula que funciona ponta a ponta hoje — ver abaixo).
//
// ATENÇÃO — várias ações desta tela dependem de rotas que não existem no backend atual:
//  - fetchContatosEmergencia (GET /api/contatos-emergencia/aluno/:id): falha
//    silenciosamente: a seção "Contatos" sempre aparece vazia, sem erro visível.
//  - handleAdicionarContato (POST /api/contatos-emergencia) e handleDeletarContato
//    (DELETE /api/contatos-emergencia/:id): falham com alert de erro visível.
//  - handleCriarMatricula (POST /api/matriculas) e handleConfirmarCancelamento
//    (DELETE /api/matriculas/:id): idem, falham com alert — matriculas.js só tem
//    rotas GET hoje. Ou seja: criar/cancelar uma matrícula individual (fora da
//    importação em massa) não funciona nesta tela.
//  - Funciona normalmente: Cadastrar Aluno, Excluir Aluno e Lançamento em Massa
//    (usam /api/alunos, que está implementado).
function GerenciarMatriculas() {
  const navigate = useNavigate();
  const { institutionId } = useInstitution();
  const { user } = useAuth();
  const isCoordenadorOuMaster = user?.perfil === 'coordenador' || user?.perfil === 'master';
  const { podeCriar, podeEditar, podeExcluir, podeExportar } =
    usePermissao('/gerenciar-matriculas');
  const podeCriarEfetivo = isCoordenadorOuMaster || podeCriar;
  const podeEditarEfetivo = isCoordenadorOuMaster || podeEditar;
  const podeExcluirEfetivo = isCoordenadorOuMaster || podeExcluir;
  const podeExportarEfetivo = isCoordenadorOuMaster || podeExportar;

  const confirm = useConfirm();
  const token = localStorage.getItem('authToken');
  const authHeaders = useMemo(
    () => ({
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );
  const [alunos, setAlunos] = useState([]);
  const [matriculas, setMatriculas] = useState([]);
  const [selectedAlunoId, setSelectedAlunoId] = useState('');
  const [alunoSelecionadoNome, setAlunoSelecionadoNome] = useState('');
  const [loadingMatriculas, setLoadingMatriculas] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [matriculaParaCancelar, setMatriculaParaCancelar] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isMatriculaModalOpen, setIsMatriculaModalOpen] = useState(false);
  const [termoBuscaAluno, setTermoBuscaAluno] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('ativo');
  // Ordenação da lista quando mostrando Inativos/Em espera — por padrão
  // nome A-Z; usuário pode alternar pra Idade (mais novo/mais velho) ou,
  // só em Inativos, pela data de inativação (ver alunosFiltradosParaSelecao).
  const [ordenacaoLista, setOrdenacaoLista] = useState({ campo: 'nome', direcao: 'asc' });
  const alternarOrdenacao = (campo) => {
    setOrdenacaoLista((prev) =>
      prev.campo === campo
        ? { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' }
        : { campo, direcao: 'asc' },
    );
  };
  // Filtro por idade e por turno (estilo Excel, ver FiltroValorColuna) — só
  // usados em Inativos/Em espera, igual à ordenação. Guardam os valores
  // ESCONDIDOS ("8 anos", "Manhã", "-" etc.), vazio = sem filtro.
  const [idadesExcluidas, setIdadesExcluidas] = useState([]);
  const [turnosExcluidos, setTurnosExcluidos] = useState([]);
  const [isDeleteAlunoModalOpen, setIsDeleteAlunoModalOpen] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [deleteMotivo, setDeleteMotivo] = useState('');
  const [modalInativar, setModalInativar] = useState({
    isOpen: false,
    alunoId: null,
    alunoNome: '',
    motivo: '',
  });
  const [isExcluidosModalOpen, setIsExcluidosModalOpen] = useState(false);
  const [alunosExcluidos, setAlunosExcluidos] = useState([]);
  const [loadingExcluidos, setLoadingExcluidos] = useState(false);
  const [restaurandoId, setRestaurandoId] = useState(null);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [alunoParaExcluirDefinitivamente, setAlunoParaExcluirDefinitivamente] = useState(null);
  const [fotoAmpliada, setFotoAmpliada] = useState(null); // { url, nome } — clique no avatar da lista
  const [permanentDeleteConfirmationInput, setPermanentDeleteConfirmationInput] = useState('');
  const [isExcluindoPermanente, setIsExcluindoPermanente] = useState(false);
  const [copyButtonText, setCopyButtonText] = useState('Copiar');
  const [fullImportLog] = useState([]);
  const [importStats, setImportStats] = useState({ sucessos: 0, falhas: 0 });

  // Estados para o progresso de importação
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // Estados para dados completos do aluno selecionado
  const [alunoDetalhes, setAlunoDetalhes] = useState(null);
  const [responsavelLegal, setResponsavelLegal] = useState(null);
  const [observacoesSaude, setObservacoesSaude] = useState([]);
  const [historicoNiveis, setHistoricoNiveis] = useState([]);
  const [situacaoAnual, setSituacaoAnual] = useState([]);
  const [contatosEmergencia, setContatosEmergencia] = useState([]);
  const [carregandoDetalhes, setCarregandoDetalhes] = useState(false);

  // Turmas disponíveis e seleção para nova matrícula
  const [turmasDisponiveis, setTurmasDisponiveis] = useState([]);
  const [turmaSelecionadaId, setTurmaSelecionadaId] = useState('');
  const [salvandoMatricula, setSalvandoMatricula] = useState(false);
  const [filtroTurmaDia, setFiltroTurmaDia] = useState('');
  const [filtroTurmaTurno, setFiltroTurmaTurno] = useState('');
  const [buscaTurmaTexto, setBuscaTurmaTexto] = useState('');

  // Modal para foto do aluno
  const [isFotoModalOpen, setIsFotoModalOpen] = useState(false);

  // Estados para o modal de alerta genérico
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertModalMessage, setAlertModalMessage] = useState('');
  const [alertModalType, setAlertModalType] = useState('info'); // 'info', 'success', 'error'

  // Função para exibir o modal de alerta
  const showAlertModal = useCallback((message, type = 'info') => {
    setAlertModalMessage(message);
    setAlertModalType(type);
    setIsAlertModalOpen(true);
  }, []);

  // Função para buscar alunos
  const fetchAlunos = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/alunos?status=${encodeURIComponent(statusFiltro)}`,
        {
          headers: authHeaders,
        },
      );
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
      const rawAlunos = await response.json();
      const alunosData = rawAlunos?.data ?? (Array.isArray(rawAlunos) ? rawAlunos : []);
      setAlunos(alunosData.sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (error) {
      console.error('Erro detalhado ao buscar alunos:', error);
      showAlertModal(
        `Erro ao carregar lista de inscritos: ${error.message}. Verifique o console.`,
        'error',
      );
    }
  }, [showAlertModal, authHeaders, statusFiltro]);

  // Muda o status de um aluno direto na lista (ex.: tirar da fila de espera,
  // reativar). PATCH de campo único (ver colunasPermitidas em alunos.js) —
  // depois recarrega a lista, já que o aluno pode sair do filtro atual
  // (ex.: mudar de "espera" pra "ativo" enquanto o filtro é "Em espera").
  const handleAlterarStatusAluno = useCallback(
    async (alunoId, novoStatus) => {
      if (novoStatus === 'inativo') {
        const alunoObj = alunos.find((a) => a.id === Number(alunoId));
        setModalInativar({
          isOpen: true,
          alunoId,
          alunoNome: alunoObj?.nome || '',
          motivo: '',
        });
        return;
      }

      try {
        // Qualquer status != "ativo" faz o backend encerrar automaticamente
        // (ver encerrarMatriculasSeNaoAtivo em status-sync.js) toda matrícula
        // ativa desse aluno — avisa ANTES, com o nome das turmas, pra quem
        // está mudando o status saber a consequência e poder desistir.
        if (novoStatus !== 'ativo') {
          const resMatriculas = await fetch(`${API_URL}/api/matriculas/aluno/${alunoId}`, {
            headers: authHeaders,
          });
          const matriculasAtivas = resMatriculas.ok ? await resMatriculas.json() : [];
          const nomesTurmas = Array.isArray(matriculasAtivas)
            ? [...new Set(matriculasAtivas.map((m) => m.nome_atividade).filter(Boolean))]
            : [];
          if (nomesTurmas.length > 0) {
            const confirmou = await confirm(
              `${nomesTurmas.length === 1 ? 'A matrícula abaixo será encerrada' : `As ${nomesTurmas.length} matrículas abaixo serão encerradas`}:\n\n${nomesTurmas.join('\n')}`,
              { title: `Marcar como "${novoStatus}"?`, confirmText: 'Continuar' },
            );
            if (!confirmou) return;
          }
        }

        const response = await fetch(`${API_URL}/api/alunos/${alunoId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ campo: 'status', valor: novoStatus }),
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.error || `Erro HTTP: ${response.status}`);
        }
        await fetchAlunos();
      } catch (error) {
        showAlertModal(`Erro ao alterar status: ${error.message}`, 'error');
      }
    },
    [authHeaders, fetchAlunos, showAlertModal, confirm, alunos],
  );

  const handleConfirmarInativacao = async () => {
    const { alunoId, motivo } = modalInativar;
    if (!motivo.trim()) {
      showAlertModal('O motivo da desistência é obrigatório ao inativar o inscrito.', 'error');
      return;
    }

    try {
      const resMatriculas = await fetch(`${API_URL}/api/matriculas/aluno/${alunoId}`, {
        headers: authHeaders,
      });
      const matriculasAtivas = resMatriculas.ok ? await resMatriculas.json() : [];
      const nomesTurmas = Array.isArray(matriculasAtivas)
        ? [...new Set(matriculasAtivas.map((m) => m.nome_atividade).filter(Boolean))]
        : [];
      if (nomesTurmas.length > 0) {
        const confirmou = await confirm(
          `${nomesTurmas.length === 1 ? 'A matrícula abaixo será encerrada' : `As ${nomesTurmas.length} matrículas abaixo serão encerradas`}:\n\n${nomesTurmas.join('\n')}`,
          { title: 'Confirmar inativação do inscrito', confirmText: 'Continuar', tone: 'danger' },
        );
        if (!confirmou) return;
      }

      const response = await fetch(`${API_URL}/api/alunos/${alunoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          campo: 'status',
          valor: 'inativo',
          motivo_desistencia: motivo.trim(),
        }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || `Erro HTTP: ${response.status}`);
      }
      setModalInativar({ isOpen: false, alunoId: null, alunoNome: '', motivo: '' });
      showAlertModal('Inscrito inativado com sucesso!', 'success');
      await fetchAlunos();
      if (selectedAlunoId && String(selectedAlunoId) === String(alunoId)) {
        await fetchAlunoCompleto(alunoId);
      }
    } catch (error) {
      showAlertModal(`Erro ao inativar inscrito: ${error.message}`, 'error');
    }
  };

  // Corrige manualmente a data de inativação (ela é preenchida sozinha pelo
  // backend ao mudar o status pra "inativo" — ver handleAlterarStatusAluno
  // acima — mas pode estar errada, ex.: aluno já estava inativo há tempo
  // antes desse campo existir, ou alguém mudou o status num dia errado).
  const handleAlterarDataInativacao = useCallback(
    async (alunoId, novaData) => {
      try {
        const response = await fetch(`${API_URL}/api/alunos/${alunoId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ campo: 'inativado_em', valor: novaData }),
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.error || `Erro HTTP: ${response.status}`);
        }
        await fetchAlunos();
      } catch (error) {
        showAlertModal(`Erro ao corrigir data de inativação: ${error.message}`, 'error');
      }
    },
    [authHeaders, fetchAlunos, showAlertModal],
  );

  // Função para buscar contatos de emergência do aluno selecionado.
  // ATENÇÃO: /api/contatos-emergencia/aluno/:id não existe no backend — ver aviso no topo do arquivo.
  const fetchContatosEmergencia = useCallback(
    async (alunoId) => {
      if (!alunoId) {
        setContatosEmergencia([]);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/contatos-emergencia/aluno/${alunoId}`, {
          headers: authHeaders,
        });
        if (response.ok) {
          const data = await response.json();
          setContatosEmergencia(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Erro ao buscar contatos de emergência:', error);
      }
    },
    [authHeaders],
  );

  // Busca turmas ativas disponíveis para nova matrícula
  const fetchTurmas = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/atividades`, { headers: authHeaders });
      if (response.ok) {
        const raw = await response.json();
        const data = raw?.data ?? (Array.isArray(raw) ? raw : []);
        setTurmasDisponiveis(data.filter((t) => !t.data_fim));
      }
    } catch (err) {
      console.error('Erro ao buscar turmas:', err);
    }
  }, [authHeaders]);

  // Busca todos os dados completos do aluno: detalhes, responsável, saúde, níveis e situação anual
  const fetchAlunoCompleto = useCallback(
    async (alunoId) => {
      if (!alunoId) {
        setAlunoDetalhes(null);
        setResponsavelLegal(null);
        setObservacoesSaude([]);
        setHistoricoNiveis([]);
        setSituacaoAnual([]);
        return;
      }
      setCarregandoDetalhes(true);
      try {
        const [resAluno, resResp, resSaude, resNiveis, resSit] = await Promise.all([
          fetch(`${API_URL}/api/alunos/${alunoId}`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${alunoId}/responsavel`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${alunoId}/saude`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${alunoId}/niveis`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${alunoId}/situacao-anual`, { headers: authHeaders }),
        ]);

        if (resAluno.ok) {
          const raw = await resAluno.json();
          setAlunoDetalhes(raw?.data ?? raw);
        }
        if (resResp.ok) {
          const raw = await resResp.json();
          setResponsavelLegal(raw?.data ?? raw);
        } else {
          setResponsavelLegal(null);
        }
        if (resSaude.ok) {
          const raw = await resSaude.json();
          const list = raw?.data ?? (Array.isArray(raw) ? raw : []);
          setObservacoesSaude(list);
        } else {
          setObservacoesSaude([]);
        }
        if (resNiveis.ok) {
          const raw = await resNiveis.json();
          const list = raw?.data ?? (Array.isArray(raw) ? raw : []);
          setHistoricoNiveis(list);
        } else {
          setHistoricoNiveis([]);
        }
        if (resSit.ok) {
          const raw = await resSit.json();
          const list = raw?.data ?? (Array.isArray(raw) ? raw : []);
          setSituacaoAnual(list);
        } else {
          setSituacaoAnual([]);
        }
      } catch (error) {
        console.error('Erro ao buscar dados completos do inscrito:', error);
      } finally {
        setCarregandoDetalhes(false);
      }
    },
    [authHeaders],
  );

  const handleAlunoSelect = (aluno) => {
    setSelectedAlunoId(String(aluno.id));
    setAlunoSelecionadoNome(aluno.nome);
    setTermoBuscaAluno(''); // Limpa o campo de busca
    fetchMatriculas(aluno.id);
    fetchContatosEmergencia(aluno.id);
    fetchAlunoCompleto(aluno.id);
  };

  // Função para buscar as matrículas do aluno selecionado
  const fetchMatriculas = useCallback(
    (alunoId) => {
      if (!alunoId) {
        setMatriculas([]);
        setAlunoSelecionadoNome('');
        return;
      }
      setLoadingMatriculas(true);
      setIsMatriculaModalOpen(false); // Esconde o modal de matrícula ao trocar de aluno
      fetch(`${API_URL}/api/matriculas/aluno/${alunoId}`, {
        headers: authHeaders,
      })
        .then((res) => res.json())
        .then((data) => {
          const list = data?.data ?? (Array.isArray(data) ? data : []);
          setMatriculas(Array.isArray(list) ? list : []);
        })
        .catch((error) => {
          console.error('Erro ao buscar matrículas:', error);
          setMatriculas([]); // Garante um array vazio em caso de erro
        })
        .finally(() => {
          setLoadingMatriculas(false);
        });
    },
    [authHeaders],
  ); // As dependências (setters de state) são estáveis e não precisam ser listadas.

  // Efeito para buscar dados iniciais (alunos, turmas e pontos de embarque)
  useEffect(() => {
    fetchAlunos();
    fetchTurmas();
    }, [fetchAlunos, fetchTurmas]);

  // Efeito para buscar as matrículas e dados completos do aluno selecionado
  useEffect(() => {
    if (selectedAlunoId) {
      fetchMatriculas(selectedAlunoId);
      fetchContatosEmergencia(selectedAlunoId);
      fetchAlunoCompleto(selectedAlunoId);
    } else {
      setAlunoDetalhes(null);
      setResponsavelLegal(null);
      setObservacoesSaude([]);
      setHistoricoNiveis([]);
      setSituacaoAnual([]);
      setMatriculas([]);
    }
  }, [selectedAlunoId, fetchMatriculas, fetchContatosEmergencia, fetchAlunoCompleto]);

  // Limpa os filtros de idade/turno ao trocar de status — evita que um
  // filtro deixado em "Inativos" (ex.: só 8 anos) esconda alunos em
  // silêncio depois de mudar pra "Em espera", onde os valores presentes
  // são outros.
  useEffect(() => {
    setIdadesExcluidas([]);
    setTurnosExcluidos([]);
  }, [statusFiltro]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });

        // Leitura da primeira aba (Alunos e Matrículas)
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json(ws, { cellDates: true });

        // Leitura de uma aba extra de "Atividades" ou "Professores"
        let atividadesData = [];
        const atividadesSheetName = wb.SheetNames.find(
          (name) => name.toLowerCase() === 'atividades' || name.toLowerCase() === 'professores',
        );

        if (atividadesSheetName) {
          atividadesData = XLSX.utils.sheet_to_json(wb.Sheets[atividadesSheetName]);
        } else if (wb.SheetNames.length > 1) {
          // Tenta ver se a segunda aba parece uma aba de atividades
          const temp = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[1]]);
          if (temp.length > 0) {
            const firstRowKeys = Object.keys(temp[0]).map((k) => k.toLowerCase().trim());
            if (
              firstRowKeys.includes('atividade') ||
              firstRowKeys.includes('professor') ||
              firstRowKeys.includes('nome da atividade')
            ) {
              atividadesData = temp;
            }
          }
        }

        processImportedData({ alunos: jsonData, atividades: atividadesData });
      } catch (error) {
        console.error('Erro ao ler arquivo Excel:', error);
        showAlertModal('Erro ao ler o arquivo. Verifique o formato.', 'error');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = ''; // Reset input
  };

  const downloadImportReport = () => {
    if (fullImportLog.length === 0) return;

    // Converte o log para o formato de planilha JSON para o Excel
    const worksheet = XLSX.utils.json_to_sheet(fullImportLog);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resultado Importação');

    XLSX.writeFile(
      workbook,
      `Relatorio_Importacao_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.xlsx`,
    );
  };

  const processImportedData = async (dataPayload) => {
    const data = Array.isArray(dataPayload) ? dataPayload : dataPayload.alunos || [];
    const atividadesExcel = Array.isArray(dataPayload) ? [] : dataPayload.atividades || [];

    if (!data || data.length === 0) {
      showAlertModal('Nenhum dado encontrado na planilha de inscritos.', 'error');
      return null;
    }

    // Bloqueia a saída da página durante o processamento para evitar corrupção de dados
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    setIsImporting(true);
    setImportProgress(10);
    setImportStats({ sucessos: 0, falhas: 0 });

    try {
      // 1. Normalização prévia de chaves para o formato que o Backend espera
      const normalizedData = data.map((row) => {
        const rawRow = {};
        Object.keys(row).forEach((key) => {
          const cleanKey = key.toLowerCase().trim().replace(/\s+/g, '_');
          let targetKey = cleanKey;
          if (cleanKey === 'aluno') targetKey = 'nome';
          // Aceita "nascimento", "data_nascimento", "data_de_nascimento" etc. —
          // antes só reconhecia o cabeçalho exato "nascimento", então uma coluna
          // chamada "Data de Nascimento" (bem comum) nunca era encontrada e a
          // data do aluno nunca chegava a ser enviada pro backend.
          //
          // BUG CORRIGIDO: esse `.includes('nascimento')` pegava TAMBÉM a coluna
          // de nascimento do RESPONSÁVEL (ex.: "Data de Nascimento do Responsável",
          // "Responsavel Data Nascimento") e mandava pro mesmo campo do aluno —
          // como a coluna do responsável normalmente vem depois no arquivo, ela
          // SOBRESCREVIA a data de nascimento real do aluno com a do responsável
          // (foi assim que várias datas de aluno viraram anos 50/60/1900 — eram
          // datas de nascimento de responsável, não erro de cálculo). Confirmado:
          // `responsavel_legal.data_nascimento` estava sempre vazio no banco,
          // porque esse valor nunca chegava a esse campo — sempre parava aqui.
          // Sem acento só pra essa comparação (não muda targetKey nos outros
          // campos) — "Responsável" com acento não batia com "responsavel" sem
          // acento, fazendo a checagem abaixo achar que TODA coluna de
          // nascimento era do aluno mesmo quando o cabeçalho dizia "Responsável".
          const cleanKeySemAcento = cleanKey.normalize('NFD').replace(/[̀-ͯ]/g, '');
          const tokensChave = cleanKeySemAcento.split('_');
          const ehResponsavel = tokensChave.includes('responsavel');
          // "DN" é a abreviação real usada na planilha original do usuário
          // ("DN RESPONSÁVEL") pra "Data de Nascimento" — sem isso, esse
          // cabeçalho específico não batia com `.includes('nascimento')` e a
          // data do responsável nunca era capturada (nem pro campo certo, nem
          // pro errado — simplesmente se perdia).
          const ehDataNascimento =
            cleanKeySemAcento.includes('nascimento') || tokensChave.includes('dn');
          if (ehDataNascimento) {
            targetKey = ehResponsavel ? 'responsavel_data_nascimento' : 'data_nascimento';
          }
          if (cleanKey === 'acompanhamento') targetKey = 'acompanhamento';
          if (cleanKey === 'ponto') targetKey = 'ponto';

          const val = row[key];
          rawRow[targetKey] = typeof val === 'string' ? val.trim() : val;

          if (targetKey === 'sexo') {
            const v = String(val || '').toUpperCase();
            if (v.startsWith('M')) rawRow[targetKey] = 'M';
            else if (v.startsWith('F')) rawRow[targetKey] = 'F';
          }
        });
        return rawRow;
      });

      // Normaliza as atividades
      const normalizedAtividades = atividadesExcel.map((row) => {
        const rawRow = {};
        Object.keys(row).forEach((key) => {
          const cleanKey = key.toLowerCase().trim().replace(/\s+/g, '_');
          rawRow[cleanKey] = typeof row[key] === 'string' ? row[key].trim() : row[key];
        });
        return rawRow;
      });

      setImportProgress(40);

      // 2. Envio em Lote Único (Bulk) - Muito mais rápido e seguro
      const response = await fetch(`${API_URL}/api/alunos/upsert-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({ alunos: normalizedData, atividades: normalizedAtividades }),
      });

      const result = await response.json();

      if (response.ok) {
        const { resumo } = result?.data ?? result;
        setImportProgress(100);
        setImportStats({ sucessos: resumo.total_recebido, falhas: 0 });
        let aviso = '';
        if (resumo.linhas_sem_nome_ignoradas > 0) {
          aviso += `\n\n⚠️ ${resumo.linhas_sem_nome_ignoradas} linha(s) sem o nome do aluno foram ignoradas — confira se a coluna de nome está preenchida nessas linhas da planilha.`;
        }
        if (resumo.niveis_fora_de_faixa_ignorados > 0) {
          aviso += `\n\n⚠️ ${resumo.niveis_fora_de_faixa_ignorados} linha(s) com valor de "nível" fora da faixa esperada (1 a 99) foram ignoradas — confira a coluna nivel dessas linhas na planilha, pode ter outro dado na célula errada.`;
        }
        if (resumo.conflitos_horario?.length > 0) {
          const lista = resumo.conflitos_horario
            .slice(0, 10)
            .map(
              (c) =>
                `  • ${c.aluno}: ${c.dia_semana} ${c.horario} — "${c.turma_1}" x "${c.turma_2}"`,
            )
            .join('\n');
          const resto =
            resumo.conflitos_horario.length > 10
              ? `\n  ... e mais ${resumo.conflitos_horario.length - 10}`
              : '';
          aviso += `\n\n⚠️ ${resumo.conflitos_horario.length} conflito(s) de horário — o mesmo inscrito apareceu no mesmo dia/horário em turmas diferentes; só a primeira foi mantida:\n${lista}${resto}`;
        }
        if (resumo.conflitos_turno?.length > 0) {
          const lista = resumo.conflitos_turno
            .slice(0, 10)
            .map(
              (c) =>
                `  • ${c.aluno}: "${c.turma}" (${c.dia_semana} ${c.horario}) é do turno ${c.turno_turma}, mas o inscrito é do turno ${c.turno_aluno}`,
            )
            .join('\n');
          const resto =
            resumo.conflitos_turno.length > 10
              ? `\n  ... e mais ${resumo.conflitos_turno.length - 10}`
              : '';
          aviso += `\n\n⚠️ ${resumo.conflitos_turno.length} conflito(s) de turno — matrícula NÃO criada:\n${lista}${resto}`;
        }
        if (resumo.turmas_sem_area?.length > 0) {
          const lista = resumo.turmas_sem_area
            .slice(0, 10)
            .map((nome) => `  • ${nome}`)
            .join('\n');
          const resto =
            resumo.turmas_sem_area.length > 10
              ? `\n  ... e mais ${resumo.turmas_sem_area.length - 10}`
              : '';
          aviso += `\n\n⚠️ ${resumo.turmas_sem_area.length} turma(s) sem prefixo de área reconhecido no nome (CAP/E.P./ESP/TEC/CUL) — foram classificadas como Cultural por padrão; confira se está certo em Turmas.js:\n${lista}${resto}`;
        }
        if (resumo.matriculas_encerradas_troca_turno > 0) {
          aviso += `\n\n↔️ ${resumo.matriculas_encerradas_troca_turno} matrícula(s) encerrada(s) porque o turno do inscrito mudou na planilha e elas ficaram incompatíveis: ${resumo.turmas_encerradas_troca_turno.join(', ')}`;
        }
        const rotuloCategoria = { alunos: 'Inscrito', professores: 'Educador' };
        ['alunos', 'professores'].forEach((categoria) => {
          const corrigidos = resumo.nomes_corrigidos?.[categoria] || [];
          if (corrigidos.length > 0) {
            const lista = corrigidos
              .slice(0, 10)
              .map((c) => `  • "${c.enviado}" → "${c.corrigido_para}"`)
              .join('\n');
            const resto = corrigidos.length > 10 ? `\n  ... e mais ${corrigidos.length - 10}` : '';
            aviso += `\n\n✏️ ${corrigidos.length} nome(s) de ${rotuloCategoria[categoria].toLowerCase()} corrigido(s) automaticamente (só diferença de acento/maiúscula/espaço do que já estava cadastrado):\n${lista}${resto}`;
          }
          const suspeitos = resumo.possiveis_duplicados?.[categoria] || [];
          if (suspeitos.length > 0) {
            const lista = suspeitos
              .slice(0, 10)
              .map((c) => `  • "${c.enviado}" parecido com "${c.parecido_com}" já cadastrado`)
              .join('\n');
            const resto = suspeitos.length > 10 ? `\n  ... e mais ${suspeitos.length - 10}` : '';
            aviso += `\n\n⚠️ ${suspeitos.length} possível(is) duplicidade(s) de ${rotuloCategoria[categoria].toLowerCase()} — nome parecido mas não idêntico a um já cadastrado; NÃO foi corrigido automaticamente (pode ser gente/turma diferente de verdade), confira manualmente:\n${lista}${resto}`;
          }
        });
        const temAviso = aviso !== '';
        showAlertModal(
          `Importação concluída com sucesso!\n\n` +
            `Inscritos processados: ${resumo.total_recebido}\n` +
            `Registros afetados (criados/atualizados): ${resumo.alunos_afetados}\n` +
            `Matrículas vinculadas: ${resumo.matriculas_afetadas}` +
            aviso,
          temAviso ? 'info' : 'success',
        );
      } else {
        throw new Error(result.error || 'Erro interno no servidor');
      }
    } catch (error) {
      console.error('Erro na importação:', error);
      setImportStats({ sucessos: 0, falhas: data.length });
      showAlertModal('Falha crítica ao importar: ' + error.message, 'error');
    } finally {
      setIsImporting(false);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      fetchAlunos();
    }
  };

  // Gera o código de acesso de 6 dígitos que o aluno usa em /aluno/entrar
  // (tela exclusiva de perfil/gamificação dele) — ver POST /api/alunos/:id/gerar-codigo.
  const handleGerarCodigoAcesso = async () => {
    if (!selectedAlunoId) return;
    try {
      const response = await fetch(`${API_URL}/api/alunos/${selectedAlunoId}/gerar-codigo`, {
        method: 'POST',
        headers: authHeaders,
      });
      const rawData = await response.json();
      const data = rawData?.data ?? rawData;
      if (!response.ok) throw new Error(rawData?.error || 'Erro ao gerar código de acesso.');
      showAlertModal(
        `Código de acesso de ${alunoSelecionadoNome}:\n\n${data.codigo_acesso}\n\nAnote e entregue ao inscrito. Gerar de novo substitui esse código.`,
        'success',
      );
    } catch (error) {
      showAlertModal(error.message, 'error');
    }
  };

  const handleCopyDeleteWord = () => {
    navigator.clipboard.writeText('deletar').then(() => {
      setCopyButtonText('Copiado!');
      setTimeout(() => setCopyButtonText('Copiar'), 2000); // Volta ao texto original após 2 segundos
    });
  };

  const handleDeleteAluno = async () => {
    if (deleteConfirmationInput !== 'deletar' || !selectedAlunoId) {
      showAlertModal('A confirmação falhou. A palavra digitada está incorreta.', 'error');
      return;
    }

    if (!deleteMotivo.trim()) {
      showAlertModal('O motivo da desistência/exclusão é obrigatório.', 'error');
      return;
    }

    setIsCanceling(true);
    try {
      const response = await fetch(`${API_URL}/api/alunos/${selectedAlunoId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ motivo_desistencia: deleteMotivo.trim() }),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || `Falha ao excluir inscrito (Status: ${response.status})`);
      }

      showAlertModal('Inscrito excluído com sucesso!', 'success');
      setIsDeleteAlunoModalOpen(false);
      setDeleteConfirmationInput('');
      setDeleteMotivo('');
      setSelectedAlunoId(''); // Limpa a seleção para voltar à tela inicial
      setAlunoSelecionadoNome('');
      fetchAlunos(); // Recarrega a lista de alunos
    } catch (error) {
      console.error('Erro em DELETE /api/alunos:', error);
      showAlertModal(error.message, 'error');
    } finally {
      setIsCanceling(false);
    }
  };

  const fetchAlunosExcluidos = useCallback(async () => {
    setLoadingExcluidos(true);
    try {
      const response = await fetch(`${API_URL}/api/alunos/excluidos`, { headers: authHeaders });
      if (!response.ok) throw new Error('Falha ao carregar inscritos excluídos.');
      const rawExcluidos = await response.json();
      setAlunosExcluidos(rawExcluidos?.data ?? (Array.isArray(rawExcluidos) ? rawExcluidos : []));
    } catch (error) {
      showAlertModal(error.message, 'error');
    } finally {
      setLoadingExcluidos(false);
    }
  }, [authHeaders, showAlertModal]);

  const abrirModalExcluidos = () => {
    setIsExcluidosModalOpen(true);
    fetchAlunosExcluidos();
  };

  // Exporta nome + telefone (do responsável, ou do próprio inscrito se ele
  // não tiver) de uma lista de alunos já filtrada em tela — mesmo padrão já
  // usado em ListaDetalhe.js e AjusteGrade.js (endpoint GET /api/alunos/telefones).
  // `chave` identifica QUAL lista está exportando, pra cada botão mostrar seu
  // próprio spinner sem travar os outros.
  const [exportandoChave, setExportandoChave] = useState(null);
  const exportarInscritosExcel = async (lista, nomeArquivo, chave) => {
    if (lista.length === 0) return;
    setExportandoChave(chave);
    try {
      const ids = lista.map((a) => a.id);
      const res = await fetch(`${API_URL}/api/alunos/telefones?ids=${ids.join(',')}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Erro ao buscar telefones para exportar.');
      const rawTelefones = await res.json();
      const telefones = rawTelefones?.data ?? (Array.isArray(rawTelefones) ? rawTelefones : []);
      const telefonePorId = new Map(telefones.map((t) => [t.id, t]));
      const linhas = lista.map((a) => {
        const t = telefonePorId.get(a.id);
        return { nome: a.nome, numero: t?.telefone_responsavel || t?.telefone_aluno || '' };
      });
      const worksheet = XLSX.utils.json_to_sheet(linhas);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Inscritos');
      XLSX.writeFile(workbook, nomeArquivo);
    } catch (error) {
      showAlertModal(error.message, 'error');
    } finally {
      setExportandoChave(null);
    }
  };

  const handleRestaurarAluno = async (aluno) => {
    if (
      !(await confirm(
        `Restaurar ${aluno.nome}? Ele volta a aparecer nas listas e buscas, mas precisa ser matriculado de novo — as matrículas antigas continuam encerradas no histórico.`,
        { title: 'Restaurar inscrito', confirmText: 'Restaurar', tone: 'primary' },
      ))
    )
      return;

    setRestaurandoId(aluno.id);
    try {
      const response = await fetch(`${API_URL}/api/alunos/${aluno.id}/restaurar`, {
        method: 'POST',
        headers: authHeaders,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Falha ao restaurar inscrito.');

      showAlertModal(`${aluno.nome} foi restaurado.`, 'success');
      setAlunosExcluidos((prev) => prev.filter((a) => a.id !== aluno.id));
      fetchAlunos();
    } catch (error) {
      showAlertModal(error.message, 'error');
    } finally {
      setRestaurandoId(null);
    }
  };

  const abrirModalExcluirDefinitivamente = (aluno) => {
    setAlunoParaExcluirDefinitivamente(aluno);
    setPermanentDeleteConfirmationInput('');
    setIsPermanentDeleteModalOpen(true);
  };

  const handleExcluirDefinitivamente = async () => {
    if (permanentDeleteConfirmationInput !== 'apagar' || !alunoParaExcluirDefinitivamente) {
      showAlertModal('A confirmação falhou. A palavra digitada está incorreta.', 'error');
      return;
    }

    setIsExcluindoPermanente(true);
    try {
      const response = await fetch(
        `${API_URL}/api/alunos/${alunoParaExcluirDefinitivamente.id}/permanente`,
        {
          method: 'DELETE',
          headers: authHeaders,
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || 'Falha ao excluir inscrito definitivamente.');

      showAlertModal(
        `${alunoParaExcluirDefinitivamente.nome} foi excluído permanentemente.`,
        'success',
      );
      setAlunosExcluidos((prev) => prev.filter((a) => a.id !== alunoParaExcluirDefinitivamente.id));
      setIsPermanentDeleteModalOpen(false);
      setAlunoParaExcluirDefinitivamente(null);
      setPermanentDeleteConfirmationInput('');
    } catch (error) {
      showAlertModal(error.message, 'error');
    } finally {
      setIsExcluindoPermanente(false);
    }
  };

  // Matricula o aluno na turma selecionada
  const handleCriarMatricula = async (e) => {
    e.preventDefault();
    if (!turmaSelecionadaId || !selectedAlunoId) {
      showAlertModal('Por favor, selecione uma turma.', 'error');
      return;
    }

    const turmaEscolhida = turmasDisponiveis.find((t) => Number(t.id) === Number(turmaSelecionadaId));
    if (!turmaEscolhida) {
      showAlertModal('Turma selecionada não encontrada.', 'error');
      return;
    }

    // Checa se o aluno já tem matrícula no mesmo dia e horário
    const conflito = matriculas.find((m) => {
      const diaM = (m.dia_semana || '').trim().replace('-feira', '');
      const diaT = (turmaEscolhida.dia_semana || '').trim().replace('-feira', '');
      return diaM === diaT && m.horario === turmaEscolhida.horario;
    });

    if (conflito) {
      const mover = await confirm(
        `O inscrito já possui a matrícula "${conflito.nome_atividade}" (${conflito.dia_semana} ${conflito.horario}).\n\nDeseja mover para "${turmaEscolhida.nome}"?`,
        { title: 'Conflito de horário', confirmText: 'Mover de Turma', tone: 'primary' },
      );
      if (!mover) return;

      setSalvandoMatricula(true);
      try {
        const response = await fetch(`${API_URL}/api/matriculas/mover`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            matricula_id: conflito.idmatricula,
            id_atividade_destino: turmaSelecionadaId,
          }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || result.message || 'Falha ao mover matrícula.');
        }
        showAlertModal('Matrícula alterada com sucesso!', 'success');
        setIsMatriculaModalOpen(false);
        setTurmaSelecionadaId('');
        fetchMatriculas(selectedAlunoId);
        fetchAlunoCompleto(selectedAlunoId);
        return;
      } catch (err) {
        showAlertModal(err.message, 'error');
        return;
      } finally {
        setSalvandoMatricula(false);
      }
    }

    setSalvandoMatricula(true);
    try {
      const response = await fetch(`${API_URL}/api/matriculas/matricular`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          aluno_id: selectedAlunoId,
          id_atividade: turmaSelecionadaId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Erro ao realizar matrícula.');
      }

      showAlertModal(result.message || 'Matrícula realizada com sucesso!', 'success');
      setIsMatriculaModalOpen(false);
      setTurmaSelecionadaId('');
      fetchMatriculas(selectedAlunoId);
      fetchAlunoCompleto(selectedAlunoId);
    } catch (error) {
      console.error('Erro ao criar matrícula:', error.message);
      showAlertModal(`Erro ao criar matrícula: ${error.message}`, 'error');
    } finally {
      setSalvandoMatricula(false);
    }
  };

  // Abre o modal de confirmação
  const abrirModalCancelamento = (idmatricula) => {
    setMatriculaParaCancelar(idmatricula);
    setIsConfirmModalOpen(true);
  };

  // Executa o cancelamento após confirmação.
  // ATENÇÃO: DELETE /api/matriculas/:id não existe no backend — ver aviso no topo do arquivo.
  const handleConfirmarCancelamento = async () => {
    if (!matriculaParaCancelar) return;

    setIsCanceling(true);
    try {
      const response = await fetch(`${API_URL}/api/matriculas/${matriculaParaCancelar}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage =
          result.message ||
          result.error ||
          `Falha ao cancelar matrícula (Status: ${response.status}).`;
        throw new Error(errorMessage);
      }

      showAlertModal(result.message || 'Matrícula cancelada com sucesso!', 'success');

      // Remove a matrícula da lista local e fecha o modal
      setMatriculas((prev) => prev.filter((m) => m.idmatricula !== matriculaParaCancelar));
      setIsConfirmModalOpen(false);
      setMatriculaParaCancelar(null);
    } catch (error) {
      console.error('Erro ao cancelar matrícula:', error.message);
      showAlertModal(error.message, 'error');
    } finally {
      setIsCanceling(false);
    }
  };

  // "Noite" (ensaio) aceita aluno de qualquer turno, então mesmo mostrando só
  // o turno do aluno, a seção de Noite precisa aparecer pra caber uma
  // eventual matrícula de ensaio dele. Também inclui qualquer turno que
  // apareça de fato nas matrículas reais do aluno — sem isso, um aluno com
  // o turno cadastrado errado (ex.: "NOITE") ou com matrículas antigas de
  // outro turno (nunca encerradas quando ele mudou de turno) tinha essas
  // matrículas somem da grade em silêncio: a seção daquele turno nunca era
  // criada, então a matrícula não batia em nenhum slot e era descartada —
  // mesmo continuando ativa no banco. Usada tanto pra montar `scheduleData`
  // quanto pra decidir quais seções desenhar na tela — as duas listas
  // precisam vir do mesmo lugar, senão um conserto num lado só não
  // aparece no outro (era exatamente esse o bug antes desse comentário).
  // Já deduplicado por valor normalizado (evita "NOITE" e "Noite" virarem
  // duas seções separadas na tela quando o turno cadastrado do aluno vem
  // em caixa diferente da usada nas turmas).

  // Filtra a lista de alunos para o seletor com base no termo de busca
  // Valores de idade que realmente aparecem entre os inativos/em espera
  // (após a busca por nome, mas antes do próprio filtro de idade — igual
  // Excel, as opções não somem conforme você desmarca outras).
  const opcoesIdade = useMemo(() => {
    if (statusFiltro !== 'inativo' && statusFiltro !== 'espera') return [];
    const base = !termoBuscaAluno
      ? alunos
      : alunos.filter((aluno) => aluno.nome.toLowerCase().includes(termoBuscaAluno.toLowerCase()));
    const valores = new Set(base.map(rotuloIdade));
    return [...valores].sort(
      (a, b) => (parseInt(a, 10) || Infinity) - (parseInt(b, 10) || Infinity),
    );
  }, [alunos, termoBuscaAluno, statusFiltro]);

  // Mesma ideia de opcoesIdade, pros valores de turno.
  const opcoesTurno = useMemo(() => {
    if (statusFiltro !== 'inativo' && statusFiltro !== 'espera') return [];
    const base = !termoBuscaAluno
      ? alunos
      : alunos.filter((aluno) => aluno.nome.toLowerCase().includes(termoBuscaAluno.toLowerCase()));
    const valores = new Set(base.map(rotuloTurno));
    return [...valores].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [alunos, termoBuscaAluno, statusFiltro]);

  const alunosFiltradosParaSelecao = useMemo(() => {
    let base = !termoBuscaAluno
      ? alunos
      : alunos.filter((aluno) => aluno.nome.toLowerCase().includes(termoBuscaAluno.toLowerCase()));

    // Ordenação/filtro por coluna (Nome/Idade/Data de Inativação) só faz
    // sentido nas listas "Inativos" e "Em espera" — nos demais status a
    // lista some de tela mesmo (só aparece com busca ativa, aí a ordem
    // padrão A-Z basta).
    if (statusFiltro !== 'inativo' && statusFiltro !== 'espera') return base;

    if (idadesExcluidas.length > 0) {
      base = base.filter((aluno) => !idadesExcluidas.includes(rotuloIdade(aluno)));
    }
    if (turnosExcluidos.length > 0) {
      base = base.filter((aluno) => !turnosExcluidos.includes(rotuloTurno(aluno)));
    }

    const { campo, direcao } = ordenacaoLista;
    const mult = direcao === 'asc' ? 1 : -1;

    // Idade: quem não tem data de nascimento cadastrada vai pro final,
    // em vez de embaralhar com quem tem idade calculável.
    if (campo === 'idade') {
      const comIdade = [];
      const semIdade = [];
      base.forEach((a) => (calcularIdade(a.data_nascimento) != null ? comIdade : semIdade).push(a));
      comIdade.sort(
        (a, b) => mult * (calcularIdade(a.data_nascimento) - calcularIdade(b.data_nascimento)),
      );
      return [...comIdade, ...semIdade];
    }
    // Data de inativação: só existe em "Inativos". Quem não tem data
    // registrada (inativado antes desse campo existir) vai pro final.
    if (campo === 'inativacao' && statusFiltro === 'inativo') {
      const semData = [];
      const comData = [];
      base.forEach((a) => (a.inativado_em ? comData : semData).push(a));
      comData.sort((a, b) => mult * a.inativado_em.localeCompare(b.inativado_em));
      return [...comData, ...semData];
    }
    // Nome (padrão)
    return [...base].sort((a, b) => mult * a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [alunos, termoBuscaAluno, statusFiltro, ordenacaoLista, idadesExcluidas, turnosExcluidos]);

  const turmasFiltradas = useMemo(() => {
    return turmasDisponiveis.filter((turma) => {
      if (filtroTurmaDia && turma.dia_semana !== filtroTurmaDia) return false;
      if (filtroTurmaTurno && turma.turno !== filtroTurmaTurno) return false;
      if (buscaTurmaTexto) {
        const termo = buscaTurmaTexto.toLowerCase();
        const nomeMatch = turma.nome?.toLowerCase().includes(termo);
        const profMatch = turma.nome_professor?.toLowerCase().includes(termo);
        if (!nomeMatch && !profMatch) return false;
      }
      return true;
    });
  }, [turmasDisponiveis, filtroTurmaDia, filtroTurmaTurno, buscaTurmaTexto]);

  // Escala semanal do aluno selecionado no formato idêntico ao Grade.js
  const escalaAlunoGrid = useMemo(() => {
    const grid = {};
    diasSemana.forEach((dia) => {
      grid[dia] = {};
      horarios.forEach((horario) => {
        grid[dia][horario] = [];
      });
    });
    matriculas.forEach((m) => {
      const diaBruto = (m.dia_semana || '').trim();
      const diaLimpo = diaBruto.replace('-feira', '');
      const horarioBruto = (m.horario || '').trim();
      if (grid[diaLimpo] && grid[diaLimpo][horarioBruto]) {
        grid[diaLimpo][horarioBruto].push(m);
      }
    });
    return grid;
  }, [matriculas]);

  const horariosVisiveisEscala = useMemo(
    () =>
      horarios.filter(
        (h) =>
          h !== 'HR 3' ||
          diasSemana.some((dia) => (escalaAlunoGrid[dia]?.[h] || []).length > 0),
      ),
    [escalaAlunoGrid],
  );

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-7xl mx-auto">
        {/* Actions bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {podeCriarEfetivo && (
            <button
              onClick={() => navigate('/gerenciar-matriculas/novo-aluno')}
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Cadastrar Inscrito
            </button>
          )}
          <button
            onClick={abrirModalExcluidos}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors"
          >
            <UserX className="w-4 h-4" />
            Alunos Excluídos
          </button>
          {podeCriarEfetivo && (
            <>
              <label
                htmlFor="excel-upload"
                className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors cursor-pointer"
              >
                <Upload className="w-4 h-4" />
                Lançamento em Massa
              </label>
              <input
                type="file"
                id="excel-upload"
                accept=".xlsx, .xls"
                className="hidden"
                onChange={handleFileUpload}
              />
            </>
          )}
          {podeEditarEfetivo && (
            <button
              onClick={() => setIsMatriculaModalOpen(true)}
              disabled={!selectedAlunoId}
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-gray-300 text-white dark:text-gray-900 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Nova Matrícula
            </button>
          )}
        </div>

        {/* Search card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-2.5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Buscar Inscrito
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
            <div className="relative flex-1 min-w-64 sm:min-w-64">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                id="aluno-search-input"
                type="text"
                placeholder="Digite o nome para encontrar..."
                value={termoBuscaAluno}
                onChange={(e) => {
                  setTermoBuscaAluno(e.target.value);
                  setSelectedAlunoId('');
                }}
                className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
            <select
              value={statusFiltro}
              onChange={(e) => setStatusFiltro(e.target.value)}
              className="h-11 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            >
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
              <option value="espera">Em espera</option>
              <option value="todos">Todos</option>
            </select>
            {(statusFiltro === 'inativo' || statusFiltro === 'espera') && (
              <div className="flex items-center gap-1.5">
                {[
                  { campo: 'nome', label: 'Nome' },
                  { campo: 'idade', label: 'Idade' },
                  ...(statusFiltro === 'inativo'
                    ? [{ campo: 'inativacao', label: 'Inativação' }]
                    : []),
                ].map(({ campo, label }) => {
                  const ativo = ordenacaoLista.campo === campo;
                  const Icone = ativo
                    ? ordenacaoLista.direcao === 'asc'
                      ? ArrowUp
                      : ArrowDown
                    : ArrowUpDown;
                  return (
                    <button
                      key={campo}
                      type="button"
                      onClick={() => alternarOrdenacao(campo)}
                      title={`Ordenar por ${label}`}
                      className={`inline-flex items-center gap-1.5 h-11 px-3 rounded-xl border text-sm font-medium transition-colors whitespace-nowrap ${
                        ativo
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icone className="w-3.5 h-3.5" />
                      {label}
                    </button>
                  );
                })}
                <span className="inline-flex items-center gap-1.5 h-11 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Idade
                  <FiltroValorColuna
                    opcoes={opcoesIdade}
                    excluidos={idadesExcluidas}
                    onChange={setIdadesExcluidas}
                    titulo="Filtrar por idade específica"
                  />
                </span>
                <span className="inline-flex items-center gap-1.5 h-11 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Turno
                  <FiltroValorColuna
                    opcoes={opcoesTurno}
                    excluidos={turnosExcluidos}
                    onChange={setTurnosExcluidos}
                    titulo="Filtrar por turno"
                  />
                </span>
              </div>
            )}
          </div>

          {(termoBuscaAluno || statusFiltro !== 'ativo') && (
            <div className="mt-4 border border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden">
              {podeExportarEfetivo && alunosFiltradosParaSelecao.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {alunosFiltradosParaSelecao.length} inscrito(s)
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      exportarInscritosExcel(alunosFiltradosParaSelecao, 'Inscritos.xlsx', 'lista')
                    }
                    disabled={exportandoChave === 'lista'}
                    title="Exporta nome e número (telefone do responsável, ou do inscrito se ele não tiver) só de quem está visível com o filtro atual"
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {exportandoChave === 'lista' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    Exportar Excel
                  </button>
                </div>
              )}
              {alunosFiltradosParaSelecao.length > 0 ? (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {alunosFiltradosParaSelecao.map((aluno) => {
                    const corNivel = corCardNivel(aluno.nivel);
                    return (
                      <div
                        key={aluno.id}
                        className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${corNivel ? `${corNivel} hover:brightness-95 dark:hover:brightness-110` : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (aluno.foto_url)
                              setFotoAmpliada({ url: aluno.foto_url, nome: aluno.nome });
                          }}
                          disabled={!aluno.foto_url}
                          title={aluno.foto_url ? 'Ampliar foto' : undefined}
                          className="w-9 h-9 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 disabled:cursor-default"
                        >
                          {aluno.foto_url ? (
                            <img
                              src={aluno.foto_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                          )}
                        </button>
                        <button
                          onClick={() => handleAlunoSelect(aluno)}
                          className="flex-1 min-w-0 text-left"
                        >
                          <div
                            className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2 flex-wrap"
                            translate="no"
                          >
                            <span>{aluno.nome}</span>
                            <AlunoBadges aluno={aluno} />
                            {Boolean(aluno.bolsista) && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                <GraduationCap className="w-3 h-3" /> Bolsista
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            {aluno.telefone && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {aluno.telefone}
                              </div>
                            )}
                            {(statusFiltro === 'inativo' || statusFiltro === 'espera') &&
                              calcularIdade(aluno.data_nascimento) != null && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {calcularIdade(aluno.data_nascimento)} anos
                                </div>
                              )}
                            {(statusFiltro === 'inativo' || statusFiltro === 'espera') &&
                              aluno.turno && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {aluno.turno}
                                </div>
                              )}
                            {statusFiltro === 'inativo' && aluno.motivo_desistencia && (
                              <div
                                className="text-xs text-red-600 dark:text-red-400 truncate max-w-xs"
                                title={aluno.motivo_desistencia}
                              >
                                Motivo: {aluno.motivo_desistencia}
                              </div>
                            )}
                          </div>
                        </button>
                        {statusFiltro === 'inativo' && (
                          <div
                            className="flex flex-col items-end mr-2 flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">
                              {aluno.inativado_em
                                ? `Inativo desde ${formatarDataBR(aluno.inativado_em)}`
                                : 'Sem data registrada'}
                            </span>
                            <input
                              type="date"
                              value={aluno.inativado_em || ''}
                              onChange={(e) =>
                                handleAlterarDataInativacao(aluno.id, e.target.value)
                              }
                              title="Corrigir data de inativação"
                              disabled={!podeEditarEfetivo}
                              className="text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 mt-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50"
                            />
                          </div>
                        )}
                        {podeEditarEfetivo && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/gerenciar-matriculas/aluno/${aluno.id}/editar`);
                            }}
                            title="Editar inscrito"
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex-shrink-0 ml-2"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <select
                          value={aluno.status || 'ativo'}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleAlterarStatusAluno(aluno.id, e.target.value)}
                          disabled={!podeEditarEfetivo}
                          className="text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1.5 ml-3 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50"
                        >
                          <option value="ativo">Ativo</option>
                          <option value="inativo">Inativo</option>
                          <option value="espera">Em espera</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhum inscrito encontrado.
                </div>
              )}
            </div>
          )}
        </div>

        {selectedAlunoId && (() => {
          const aluno = alunoDetalhes || alunos.find((a) => a.id === Number(selectedAlunoId)) || {};
          const nivelAtual =
            historicoNiveis.find((n) => !n.data_fim) ||
            historicoNiveis[0] ||
            (aluno.nivel ? { nivel: aluno.nivel, subnivel: aluno.subnivel } : null);
          const sitAtual = situacaoAnual[0] || null;

          return (
            <div className="space-y-6 mb-8">
              {/* Header do Aluno com Foto, Dados Principais e Ações */}
              <div className="p-4 sm:p-5 rounded-3xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Foto e Informações Principais */}
                  <div className="flex items-start sm:items-center gap-4">
                    <div className="relative group flex-shrink-0">
                      <div
                        onClick={() => {
                          if (aluno.foto_url) setFotoAmpliada({ url: aluno.foto_url, nome: aluno.nome });
                        }}
                        className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 border-white dark:border-gray-800 shadow-md flex items-center justify-center bg-gray-100 dark:bg-gray-800 ${aluno.foto_url ? 'cursor-pointer hover:opacity-90' : ''}`}
                        title={aluno.foto_url ? 'Clique para ampliar foto' : undefined}
                      >
                        {aluno.foto_url ? (
                          <img src={aluno.foto_url} alt={aluno.nome} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                        )}
                      </div>
                      {podeEditarEfetivo && (
                        <button
                          type="button"
                          onClick={() => setIsFotoModalOpen(true)}
                          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                          title="Alterar foto do inscrito"
                        >
                          <Camera className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-1.5 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 truncate" translate="no">
                          {aluno.nome || alunoSelecionadoNome}
                        </h2>
                        {/* Status Badge */}
                        {aluno.status === 'ativo' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                            <CheckCircle2 className="w-3 h-3" /> Ativo
                          </span>
                        )}
                        {aluno.status === 'espera' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                            <Clock className="w-3 h-3" /> Em Espera
                          </span>
                        )}
                        {aluno.status === 'inativo' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                            <AlertCircle className="w-3 h-3" /> Inativo
                          </span>
                        )}
                        {/* Nível Atual Badge */}
                        {nivelAtual?.nivel && (
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${corCardNivel(nivelAtual.nivel)}`}>
                            Nível {nivelAtual.nivel}{nivelAtual.subnivel ? ` · ${nivelAtual.subnivel}` : ''}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        {aluno.turno && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 font-medium text-gray-700 dark:text-gray-300">
                            Turno: {aluno.turno}
                          </span>
                        )}
                        {aluno.data_nascimento && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {rotuloIdade(aluno)} ({formatarDataBR(aluno.data_nascimento)})
                          </span>
                        )}
                        {aluno.telefone && (
                          <a
                            href={`tel:${aluno.telefone}`}
                            className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            {aluno.telefone}
                          </a>
                        )}
                        {aluno.codigo_acesso && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-purple-50 dark:bg-purple-900/30 font-mono text-purple-700 dark:text-purple-300 font-semibold">
                            <KeyRound className="w-3 h-3" />
                            Cód: {aluno.codigo_acesso}
                          </span>
                        )}
                      </div>
                      {aluno.status === 'inativo' && aluno.motivo_desistencia && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-300 text-xs mt-1">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>
                            <strong>Motivo da desistência:</strong> {aluno.motivo_desistencia}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ações do Topo */}
                  <div className="flex flex-wrap items-center gap-2">
                    {podeEditarEfetivo && (
                      <button
                        type="button"
                        onClick={() => navigate(`/gerenciar-matriculas/aluno/${selectedAlunoId}/editar`)}
                        className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-sm transition-colors"
                        title="Abrir página oficial de edição completa com todos os campos e histórico"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Editar Cadastro Completo
                      </button>
                    )}
                    {podeEditarEfetivo && (
                      <button
                        type="button"
                        onClick={() => {
                          setFiltroTurmaDia('');
                          setFiltroTurmaTurno(aluno.turno || '');
                          setBuscaTurmaTexto('');
                          setTurmaSelecionadaId('');
                          setIsMatriculaModalOpen(true);
                        }}
                        className="inline-flex items-center gap-2 px-3.5 h-10 rounded-xl bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-gray-900 text-xs sm:text-sm font-medium transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Nova Matrícula
                      </button>
                    )}
                    {podeEditarEfetivo && (
                      <button
                        type="button"
                        onClick={handleGerarCodigoAcesso}
                        className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300 text-xs sm:text-sm font-medium transition-colors"
                        title="Gera novo código de acesso de 6 dígitos para o aluno"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        Gerar Código
                      </button>
                    )}
                    {podeExcluirEfetivo && (
                      <button
                        type="button"
                        onClick={() => setIsDeleteAlunoModalOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 text-xs sm:text-sm font-medium transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Excluir
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {/* Escala Semanal do Inscrito — Amostra idêntica à página de Grade */}
              <div className="w-full bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100">
                      Escala semanal — {aluno.nome || alunoSelecionadoNome}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {matriculas.length} {matriculas.length === 1 ? 'matrícula' : 'matrículas'}
                    </span>
                    {podeEditarEfetivo && (
                      <button
                        type="button"
                        onClick={() => {
                          setFiltroTurmaDia('');
                          setFiltroTurmaTurno(aluno.turno || '');
                          setBuscaTurmaTexto('');
                          setTurmaSelecionadaId('');
                          setIsMatriculaModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-semibold transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Adicionar Matrícula
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-3 sm:p-4">
                  {loadingMatriculas ? (
                    <div className="flex items-center justify-center py-8 text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      <span className="text-xs">Carregando escala semanal...</span>
                    </div>
                  ) : matriculas.length === 0 ? (
                    <div className="py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                      Nenhuma turma matriculada para este inscrito no momento.
                    </div>
                  ) : (
                    <div className="overflow-x-auto -mx-3 sm:mx-0">
                      <table className="w-full text-xs border-collapse">
                        <thead className="bg-gray-50 dark:bg-gray-800/60">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 w-16">
                              Horário
                            </th>
                            {diasSemana.map((dia) => (
                              <th
                                key={dia}
                                className="px-2 py-2 text-left font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 min-w-[132px]"
                              >
                                <span className="inline-flex items-center gap-1.5">{dia}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {horariosVisiveisEscala.map((horario) => (
                            <tr
                              key={horario}
                              className="border-b border-gray-50 dark:border-gray-800 last:border-0"
                            >
                              <td className="px-3 py-2 font-medium text-gray-400 dark:text-gray-500 align-top">
                                {horario}
                              </td>
                              {diasSemana.map((dia) => {
                                const turmas = escalaAlunoGrid[dia]?.[horario] || [];
                                return (
                                  <td key={dia} className="px-1.5 py-2 align-top min-w-[132px]">
                                    {turmas.length === 0 ? (
                                      <span className="text-gray-300 dark:text-gray-700">—</span>
                                    ) : (
                                      <div className="space-y-1">
                                        {turmas.map((m) => {
                                          const cor = corDaAtividade(m.nome_atividade || m.turma_nome);
                                          return (
                                            <div
                                              key={m.idmatricula || m.id}
                                              className={`group relative px-2 py-1 rounded-lg border flex flex-col justify-center min-h-[44px] ${cor.bg} ${cor.border}`}
                                            >
                                              <div className="flex items-center justify-between gap-1">
                                                <p
                                                  className={`text-xs font-medium truncate ${cor.text}`}
                                                  translate="no"
                                                  title={m.nome_atividade || m.turma_nome}
                                                >
                                                  {m.nome_atividade || m.turma_nome || 'Turma'}
                                                </p>
                                                {podeEditarEfetivo && (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      abrirModalCancelamento(m.idmatricula || m.id)
                                                    }
                                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity p-0.5"
                                                    title="Cancelar matrícula nesta turma"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                )}
                                              </div>
                                              {(m.nome_professor || m.professor_nome) && (
                                                <p
                                                  className="text-[10px] text-gray-500 dark:text-gray-400 truncate"
                                                  translate="no"
                                                >
                                                  {m.nome_professor || m.professor_nome}
                                                </p>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Biografia do Aluno — Exibição Limpa e Estruturada (Somente Leitura) */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-1">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                      <Info className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                        Biografia e Ficha do Inscrito
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Visualização dos dados cadastrais consolidados
                      </p>
                    </div>
                  </div>
                  {podeEditarEfetivo && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/gerenciar-matriculas/aluno/${selectedAlunoId}/editar`)
                      }
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold shadow-sm transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                      Editar na Ficha Completa
                    </button>
                  )}
                </div>

                {carregandoDetalhes ? (
                  <div className="flex items-center justify-center py-12 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin mr-3 text-blue-600" />
                    <span>Carregando dados completos do inscrito...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 1. Dados Pessoais & Cadastrais */}
                    <div className="p-5 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
                      <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                          <User className="w-4 h-4" />
                        </div>
                        <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Dados Pessoais & Cadastrais
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Nome Completo</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.nome || '-'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Data de Nasc. / Idade</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {formatarDataBR(aluno.data_nascimento) || '-'} {aluno.data_nascimento && `(${rotuloIdade(aluno)})`}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Sexo</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.sexo || 'Não informado'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Telefone Principal</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.telefone || 'Não informado'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Status no Sistema</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100 capitalize">{aluno.status || 'Ativo'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Data de Inativação</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{formatarDataBR(aluno.inativado_em) || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Data de Cadastro</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{formatarDataBR(aluno.data_cadastro) || '-'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Código de Acesso</span>
                          <span className="font-mono font-bold text-purple-600 dark:text-purple-400">{aluno.codigo_acesso || 'Não gerado'}</span>
                        </div>
                      </div>
                    </div>

                    {/* 2. Dados Escolares & Transporte */}
                    <div className="p-5 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
                      <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                        <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                          <GraduationCap className="w-4 h-4" />
                        </div>
                        <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Dados Escolares & Transporte
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Condição de Bolsista</span>
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-1 ${
                            aluno.bolsista
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}>
                            <GraduationCap className="w-3.5 h-3.5" />
                            {aluno.bolsista ? 'Bolsista' : 'Não bolsista'}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Líder Servidor</span>
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-1 ${
                            aluno.lider_servidor
                              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}>
                            <span>⭐</span>
                            {aluno.lider_servidor ? 'Líder Servidor' : 'Não é líder'}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Turno Escolar</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.turno || 'Não informado'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Turma / Série</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.turma || 'Não informada'}</span>
                        </div>
                        <div className="sm:col-span-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Escola Regular Atual</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.escola_atual || 'Não informada'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Transporte</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.transporte || 'Não especificado'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Ponto de Embarque</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.ponto || 'Não definido'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Ponto de Desembarque</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {aluno.ponto_desembarque || aluno.ponto || 'Não definido'}
                          </span>
                        </div>
                        <div className="sm:col-span-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Acompanhamento</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.acompanhamento || 'Não informado'}</span>
                        </div>
                        <div className="sm:col-span-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">Observações Gerais</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{aluno.Inf || aluno.informacoes_gerais || 'Nenhuma observação informada'}</span>
                        </div>
                      </div>
                    </div>

                    {/* 3. Responsável Legal */}
                    <div className="p-5 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
                      <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                        <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                          <Users className="w-4 h-4" />
                        </div>
                        <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Responsável Legal
                        </h4>
                      </div>
                      {responsavelLegal?.nome ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                          <div className="sm:col-span-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">Nome do Responsável</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100 text-base" translate="no">
                              {responsavelLegal.nome}
                            </span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">CPF</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{responsavelLegal.cpf || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">RG</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{responsavelLegal.rg || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">Data de Nascimento</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatarDataBR(responsavelLegal.data_nascimento) || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">Telefone</span>
                            {responsavelLegal.telefone ? (
                              <a href={`tel:${responsavelLegal.telefone}`} className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                                {responsavelLegal.telefone}
                              </a>
                            ) : (
                              <span className="font-semibold text-gray-900 dark:text-gray-100">-</span>
                            )}
                          </div>
                          <div className="sm:col-span-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">E-mail</span>
                            {responsavelLegal.email ? (
                              <a href={`mailto:${responsavelLegal.email}`} className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                                {responsavelLegal.email}
                              </a>
                            ) : (
                              <span className="font-semibold text-gray-900 dark:text-gray-100">-</span>
                            )}
                          </div>
                          <div className="sm:col-span-2 pt-1 border-t border-gray-100 dark:border-gray-800">
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">Endereço</span>
                            <p className="font-semibold text-gray-900 dark:text-gray-100 mt-0.5">
                              {responsavelLegal.endereco || 'Endereço não informado'}
                              {responsavelLegal.bairro && ` · Bairro: ${responsavelLegal.bairro}`}
                              {responsavelLegal.cep && ` · CEP: ${responsavelLegal.cep}`}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-500 py-4">
                          Nenhum responsável legal cadastrado. Edite o cadastro completo para adicionar.
                        </p>
                      )}
                    </div>

                    {/* 4. Nível Atual & Situação Anual */}
                    <div className="p-5 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
                      <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                        <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                          <Award className="w-4 h-4" />
                        </div>
                        <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Nível & Situação Anual
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="p-3 rounded-2xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                          <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">
                            Nível Atual
                          </span>
                          {nivelAtual?.nivel ? (
                            <span className={`inline-block px-2.5 py-0.5 rounded-lg text-xs font-bold border ${corCardNivel(nivelAtual.nivel)}`}>
                              Nível {nivelAtual.nivel}{nivelAtual.subnivel ? ` · ${nivelAtual.subnivel}` : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Não informado</span>
                          )}
                        </div>
                        <div className="p-3 rounded-2xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                          <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">
                            Matrícula ({sitAtual?.ano || new Date().getFullYear()})
                          </span>
                          <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                            {sitAtual?.situacao_matricula || 'Regular'}
                          </span>
                        </div>
                        <div className="p-3 rounded-2xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                          <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">
                            Dívida / Finanças
                          </span>
                          <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                            {sitAtual?.situacao_divida || 'Regular'}
                          </span>
                        </div>
                      </div>

                      {historicoNiveis.length > 0 && (
                        <div className="pt-2">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-2">
                            Histórico de Graduações ({historicoNiveis.length})
                          </span>
                          <div className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
                            <table className="w-full text-xs text-left">
                              <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 border-b border-gray-100 dark:border-gray-800">
                                <tr>
                                  <th className="px-3 py-2">Nível</th>
                                  <th className="px-3 py-2">Subnível</th>
                                  <th className="px-3 py-2">Período</th>
                                  <th className="px-3 py-2">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {historicoNiveis.map((n) => (
                                  <tr key={n.id}>
                                    <td className="px-3 py-2 font-bold text-gray-900 dark:text-gray-100">Nível {n.nivel}</td>
                                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{n.subnivel || '-'}</td>
                                    <td className="px-3 py-2 text-gray-500">
                                      {formatarDataBR(n.data_inicio) || '-'} a {formatarDataBR(n.data_fim) || 'Atual'}
                                    </td>
                                    <td className="px-3 py-2">
                                      {!n.data_fim ? (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                          Atual
                                        </span>
                                      ) : (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                          Concluído
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 5. Saúde & Cuidados Especiais */}
                    <div className="p-5 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
                      <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                        <div className="w-7 h-7 rounded-lg bg-rose-50 dark:bg-rose-900/30 flex items-center justify-center text-rose-600 dark:text-rose-400">
                          <HeartPulse className="w-4 h-4" />
                        </div>
                        <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Saúde & Cuidados Especiais ({observacoesSaude.length})
                        </h4>
                      </div>

                      {(parseLaudos(aluno.laudos).length > 0 || aluno.cuidados_medicos) && (
                        <div className="p-3.5 rounded-2xl bg-gray-50/80 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 space-y-2">
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 block">
                            Laudos e Condições Ativas (Visíveis aos Educadores):
                          </span>
                          <AlunoBadges aluno={aluno} />
                          {aluno.cuidados_medicos && (
                            <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/50 dark:border-amber-800/40 rounded-lg p-2 font-medium">
                              💡 {aluno.cuidados_medicos}
                            </p>
                          )}
                        </div>
                      )}
                      {observacoesSaude.length === 0 ? (
                        <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-500 py-4">
                          Nenhuma observação de saúde ou restrição médica registrada para este inscrito.
                        </p>
                      ) : (
                        <div className="divide-y divide-gray-100 dark:border-gray-800 border border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden">
                          {observacoesSaude.map((obs) => (
                            <div key={obs.id} className="p-3.5 space-y-1">
                              <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 leading-relaxed">
                                {obs.descricao}
                              </p>
                              <p className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Registrado em {formatarDataBR(String(obs.created_at || '').slice(0, 10)) || 'Data não informada'}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 6. Contatos de Emergência */}
                    <div className="p-5 rounded-3xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
                      <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                        <div className="w-7 h-7 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400">
                          <Phone className="w-4 h-4" />
                        </div>
                        <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Contatos de Emergência
                        </h4>
                      </div>
                      {aluno.telefone && (
                        <div className="p-3 rounded-xl bg-blue-50/60 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 flex items-center justify-between">
                          <div>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">Telefone Principal</p>
                            <a href={`tel:${aluno.telefone}`} className="text-xs sm:text-sm font-bold text-gray-900 dark:text-gray-100 hover:text-blue-600">
                              {aluno.telefone}
                            </a>
                          </div>
                          <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded-md">
                            Principal
                          </span>
                        </div>
                      )}
                      {contatosEmergencia.length === 0 ? (
                        <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-500 py-2">
                          Nenhum contato adicional de emergência cadastrado.
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                          {contatosEmergencia.map((contato) => (
                            <div key={contato.id} className="p-3 rounded-xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800">
                              <p className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate" translate="no">
                                {contato.nome}
                              </p>
                              <a href={`tel:${contato.telefone}`} className="text-xs text-blue-600 dark:text-blue-400 hover:underline block mt-0.5">
                                {contato.telefone}
                              </a>
                              {contato.parentesco && (
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 block mt-0.5">
                                  Parentesco: {contato.parentesco}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </main>

      {/* Modais */}

      {/* Modal de Nova Matrícula Funcional com Turmas Reais */}
      <Modal
        isOpen={isMatriculaModalOpen}
        onClose={() => {
          setIsMatriculaModalOpen(false);
          setTurmaSelecionadaId('');
        }}
        title="Nova Matrícula"
        maxWidth="lg"
      >
        <form onSubmit={handleCriarMatricula} className="space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Selecione uma turma disponível para matricular{' '}
            <strong className="text-gray-900 dark:text-gray-100" translate="no">
              {alunoDetalhes?.nome || alunoSelecionadoNome}
            </strong>
            .
          </p>

          {/* Filtros rápidos da lista de turmas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Select
              label="Dia da Semana"
              value={filtroTurmaDia}
              onChange={(e) => setFiltroTurmaDia(e.target.value)}
              options={[
                { value: '', label: 'Todos os Dias' },
                ...diasSemana.map((d) => ({ value: d, label: d })),
              ]}
            />
            <Select
              label="Turno"
              value={filtroTurmaTurno}
              onChange={(e) => setFiltroTurmaTurno(e.target.value)}
              options={[
                { value: '', label: 'Todos os Turnos' },
                ...turnos.map((t) => ({ value: t, label: t })),
              ]}
            />
            <Input
              label="Buscar Turma / Educador"
              value={buscaTurmaTexto}
              onChange={(e) => setBuscaTurmaTexto(e.target.value)}
              placeholder="Digite o nome..."
            />
          </div>

          {/* Lista de Turmas Disponíveis */}
          <div className="space-y-1.5 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl p-2 bg-gray-50/50 dark:bg-gray-800/40">
            {turmasFiltradas.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">
                Nenhuma turma encontrada com os filtros selecionados.
              </p>
            ) : (
              turmasFiltradas.map((turma) => {
                const selecionada = Number(turmaSelecionadaId) === Number(turma.id);
                const jaMatriculado = matriculas.some(
                  (m) => Number(m.idatividades) === Number(turma.id),
                );
                return (
                  <label
                    key={turma.id}
                    className={`flex items-start gap-3 p-2.5 rounded-xl border cursor-pointer transition-colors ${selecionada ? 'bg-blue-50/70 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700' : 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
                  >
                    <input
                      type="radio"
                      name="turma_selecionada"
                      value={turma.id}
                      checked={selecionada}
                      onChange={() => setTurmaSelecionadaId(String(turma.id))}
                      disabled={jaMatriculado}
                      className="mt-1 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                          translate="no"
                        >
                          {turma.nome}
                        </span>
                        {jaMatriculado && (
                          <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
                            Já matriculado
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {turma.dia_semana} · {turma.horario} ({turma.turno})
                        </span>
                        {turma.nome_professor && <span>· Educador: {turma.nome_professor}</span>}
                        {turma.total_alunos != null && (
                          <span>· {turma.total_alunos} inscrito(s)</span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsMatriculaModalOpen(false);
                setTurmaSelecionadaId('');
              }}
              disabled={salvandoMatricula}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!turmaSelecionadaId || salvandoMatricula}
              icon={salvandoMatricula ? Loader2 : Plus}
            >
              {salvandoMatricula ? 'Matriculando...' : 'Confirmar Matrícula'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Foto do Aluno */}
      <Modal
        isOpen={isFotoModalOpen}
        onClose={() => setIsFotoModalOpen(false)}
        title="Foto do Inscrito"
      >
        <div className="space-y-4">
          <FotoAluno
            alunoId={selectedAlunoId}
            fotoUrl={
              alunoDetalhes?.foto_url ||
              alunos.find((a) => a.id === Number(selectedAlunoId))?.foto_url
            }
            authHeaders={authHeaders}
            onFotoAtualizada={(novaUrl) => {
              setAlunoDetalhes((prev) => (prev ? { ...prev, foto_url: novaUrl } : prev));
              fetchAlunos();
            }}
            disabled={!podeEditarEfetivo}
          />
          <div className="flex justify-end pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsFotoModalOpen(false)}>
              Fechar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteAlunoModalOpen}
        onClose={() => {
          setIsDeleteAlunoModalOpen(false);
          setDeleteConfirmationInput('');
          setDeleteMotivo('');
        }}
        title="Confirmar Exclusão"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            O inscrito e suas matrículas ativas somem de todas as listas e buscas do sistema (as
            matrículas passam a fazer parte do histórico). Dá pra restaurar depois em "Inscritos
            Excluídos". Para confirmar, informe o motivo e digite <strong>deletar</strong> no campo abaixo.
          </p>
          <Input
            label="Motivo da Desistência / Exclusão*"
            value={deleteMotivo}
            onChange={(e) => setDeleteMotivo(e.target.value)}
            placeholder="Informe o motivo da exclusão ou desistência..."
            required
          />
          <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">deletar</span>
            <button
              onClick={handleCopyDeleteWord}
              className="ml-auto text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
              {copyButtonText}
            </button>
          </div>
          <Input
            value={deleteConfirmationInput}
            onChange={(e) => setDeleteConfirmationInput(e.target.value)}
            placeholder="Digite a palavra de confirmação"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsDeleteAlunoModalOpen(false);
                setDeleteConfirmationInput('');
                setDeleteMotivo('');
              }}
              disabled={isCanceling}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDeleteAluno}
              disabled={deleteConfirmationInput !== 'deletar' || !deleteMotivo.trim() || isCanceling}
            >
              {isCanceling ? 'Excluindo...' : 'Confirmar Exclusão'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={modalInativar.isOpen}
        onClose={() => setModalInativar({ isOpen: false, alunoId: null, alunoNome: '', motivo: '' })}
        title="Informar Motivo da Desistência"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Você está alterando o status de{' '}
            <strong translate="no">{modalInativar.alunoNome || 'este inscrito'}</strong> para{' '}
            <strong>Inativo</strong>. Todas as matrículas ativas serão encerradas.
            É obrigatório informar o motivo da desistência.
          </p>
          <Input
            label="Motivo da Desistência*"
            value={modalInativar.motivo}
            onChange={(e) => setModalInativar((prev) => ({ ...prev, motivo: e.target.value }))}
            placeholder="Ex: Mudança de cidade, incompatibilidade de horários, motivos pessoais..."
            required
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModalInativar({ isOpen: false, alunoId: null, alunoNome: '', motivo: '' })}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirmarInativacao}
              disabled={!modalInativar.motivo.trim()}
            >
              Confirmar Inativação
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isExcluidosModalOpen}
        onClose={() => setIsExcluidosModalOpen(false)}
        title="Inscritos Excluídos"
        maxWidth="lg"
      >
        <div className="space-y-1">
          {loadingExcluidos ? (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-3" />
            </div>
          ) : alunosExcluidos.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
              Nenhum inscrito excluído.
            </p>
          ) : (
            <div>
              {podeExportarEfetivo && (
                <div className="flex items-center justify-between px-2 pb-2 mb-1 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {alunosExcluidos.length} inscrito(s)
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      exportarInscritosExcel(
                        alunosExcluidos,
                        'Inscritos_Excluidos.xlsx',
                        'excluidos',
                      )
                    }
                    disabled={exportandoChave === 'excluidos'}
                    title="Exporta nome e número (telefone do responsável, ou do inscrito se ele não tiver)"
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {exportandoChave === 'excluidos' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    Exportar Excel
                  </button>
                </div>
              )}
              <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-96 overflow-y-auto">
                {alunosExcluidos.map((aluno) => (
                  <div
                    key={aluno.id}
                    className={`flex items-center justify-between gap-3 px-2 py-3 rounded-xl ${corCardNivel(aluno.nivel)}`}
                  >
                    <div className="min-w-0">
                      <p
                        className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                        translate="no"
                      >
                        {aluno.nome}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Excluído{' '}
                        {new Date(aluno.excluido_em).toLocaleDateString('pt-BR', {
                          timeZone: 'America/Sao_Paulo',
                        })}
                        {aluno.excluido_por_nome && (
                          <>
                            {' '}
                            por <span translate="no">{aluno.excluido_por_nome}</span>
                          </>
                        )}
                      </p>
                      {aluno.motivo_desistencia && (
                        <p
                          className="text-xs text-red-600 dark:text-red-400 truncate max-w-md mt-0.5"
                          title={aluno.motivo_desistencia}
                        >
                          Motivo: {aluno.motivo_desistencia}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {podeEditarEfetivo && (
                        <Button
                          type="button"
                          variant="secondary"
                          icon={RotateCcw}
                          disabled={restaurandoId === aluno.id}
                          onClick={() => handleRestaurarAluno(aluno)}
                        >
                          {restaurandoId === aluno.id ? 'Restaurando...' : 'Restaurar'}
                        </Button>
                      )}
                      {podeExcluirEfetivo && (
                        <Button
                          type="button"
                          variant="danger"
                          icon={Trash2}
                          onClick={() => abrirModalExcluirDefinitivamente(aluno)}
                        >
                          Excluir definitivamente
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isPermanentDeleteModalOpen}
        onClose={() => setIsPermanentDeleteModalOpen(false)}
        title="Excluir Definitivamente"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Isso apaga <span translate="no">{alunoParaExcluirDefinitivamente?.nome}</span> e{' '}
            <strong>tudo</strong> relacionado a ele (matrículas, presenças, níveis, situação anual,
            saúde, responsável legal, atos de caráter, notificações) para sempre. Não dá pra
            restaurar depois. Para confirmar, digite <strong>apagar</strong> no campo abaixo.
          </p>
          <Input
            value={permanentDeleteConfirmationInput}
            onChange={(e) => setPermanentDeleteConfirmationInput(e.target.value)}
            placeholder="Digite a palavra de confirmação"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsPermanentDeleteModalOpen(false)}
              disabled={isExcluindoPermanente}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleExcluirDefinitivamente}
              disabled={permanentDeleteConfirmationInput !== 'apagar' || isExcluindoPermanente}
            >
              {isExcluindoPermanente ? 'Excluindo...' : 'Excluir para sempre'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!fotoAmpliada}
        onClose={() => setFotoAmpliada(null)}
        title={fotoAmpliada?.nome || 'Foto'}
        maxWidth="md"
      >
        {fotoAmpliada && (
          <img
            src={fotoAmpliada.url}
            alt=""
            className="w-full max-h-[75vh] object-contain rounded-2xl"
          />
        )}
      </Modal>

      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title="Confirmar Ação"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Tem certeza que deseja cancelar esta matrícula?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsConfirmModalOpen(false)}
              disabled={isCanceling}
            >
              Voltar
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirmarCancelamento}
              disabled={isCanceling}
            >
              {isCanceling ? 'Cancelando...' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isImporting}
        onClose={() => {}}
        title={importProgress < 100 ? 'Processando Importação' : 'Importação Concluída'}
      >
        <div className="space-y-4">
          {importProgress < 100 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Por favor, não feche esta aba até a conclusão.
            </p>
          ) : (
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Resultado:{' '}
              <span className="text-green-600 dark:text-green-400">
                {importStats.sucessos} Sucessos
              </span>{' '}
              | <span className="text-red-600 dark:text-red-400">{importStats.falhas} Falhas</span>
            </p>
          )}
          <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${importProgress}%` }}
            ></div>
          </div>
          <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-300">
            {importProgress}%
          </p>
          {importProgress === 100 && (
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsImporting(false);
                  setImportProgress(0);
                }}
              >
                Fechar
              </Button>
              <Button
                type="button"
                variant="primary"
                icon={FileSpreadsheet}
                onClick={downloadImportReport}
              >
                Baixar Relatório
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isAlertModalOpen}
        onClose={() => setIsAlertModalOpen(false)}
        title={
          alertModalType === 'success'
            ? 'Sucesso!'
            : alertModalType === 'error'
              ? 'Erro'
              : 'Atenção'
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            {alertModalType === 'success' && (
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
            )}
            {alertModalType === 'error' && <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />}
            {alertModalType === 'info' && <Info className="w-5 h-5 text-blue-500 mt-0.5" />}
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-[300px] overflow-y-auto">
              {alertModalMessage}
            </p>
          </div>
          <div className="flex justify-end">
            <Button type="button" variant="primary" onClick={() => setIsAlertModalOpen(false)}>
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default GerenciarMatriculas;
